/**
 * Data caching service with 12-hour revalidation
 * Fetches and caches TCG data from the API
 */

const CACHE_DURATION = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

class DataCacheService {
    constructor() {
        this.cacheKeys = {
            TCG_STATS: 'tcg_stats_cache',
            TCG_SAMPLES: 'tcg_samples_cache',
            DOC_ENDPOINTS: 'doc_endpoints_cache',
            DOC_EXAMPLES: 'doc_examples_cache',
            LAST_FETCH: 'data_last_fetch'
        };
    }

    /**
     * Check if cache is still valid (< 12 hours old)
     */
    isCacheValid() {
        const lastFetch = localStorage.getItem(this.cacheKeys.LAST_FETCH);
        if (!lastFetch) return false;

        const age = Date.now() - parseInt(lastFetch);
        return age < CACHE_DURATION;
    }

    /**
     * Get cached data if valid
     */
    getCachedData(key) {
        if (!this.isCacheValid()) {
            return null;
        }

        const cached = localStorage.getItem(key);
        if (!cached) return null;

        try {
            return JSON.parse(cached);
        } catch (error) {
            console.error('Failed to parse cached data:', error);
            return null;
        }
    }

    /**
     * Get cached data even if expired (stale-while-revalidate).
     * Returns { data, isStale } or null if nothing is cached at all.
     */
    getStaleData(key) {
        const cached = localStorage.getItem(key);
        if (!cached) return null;

        try {
            return {
                data: JSON.parse(cached),
                isStale: !this.isCacheValid()
            };
        } catch (error) {
            return null;
        }
    }

    /**
     * Set cache data with timestamp
     */
    setCacheData(key, data) {
        try {
            localStorage.setItem(key, JSON.stringify(data));
            localStorage.setItem(this.cacheKeys.LAST_FETCH, Date.now().toString());
        } catch (error) {
            console.error('Failed to cache data:', error);
        }
    }

    /**
     * Clear all cached data
     */
    clearCache() {
        Object.values(this.cacheKeys).forEach(key => {
            localStorage.removeItem(key);
        });
    }

    /**
     * Get time until next revalidation
     */
    getTimeUntilRevalidation() {
        const lastFetch = localStorage.getItem(this.cacheKeys.LAST_FETCH);
        if (!lastFetch) return 0;

        const age = Date.now() - parseInt(lastFetch);
        const remaining = CACHE_DURATION - age;
        return Math.max(0, remaining);
    }

    /**
     * Get cache status
     */
    getCacheStatus() {
        const lastFetch = localStorage.getItem(this.cacheKeys.LAST_FETCH);
        if (!lastFetch) {
            return {
                isValid: false,
                lastFetch: null,
                nextRevalidation: null
            };
        }

        const lastFetchDate = new Date(parseInt(lastFetch));
        const nextRevalidation = new Date(parseInt(lastFetch) + CACHE_DURATION);

        return {
            isValid: this.isCacheValid(),
            lastFetch: lastFetchDate,
            nextRevalidation: nextRevalidation
        };
    }
}

const dataCacheService = new DataCacheService();

export default dataCacheService;
